// Map countries to their available timezones
export const countryTimezones: { [key: string]: string[] } = {
  // United States
  'US': [
    'America/New_York',      // Eastern
    'America/Chicago',       // Central
    'America/Denver',        // Mountain
    'America/Phoenix',       // Arizona (no DST)
    'America/Los_Angeles',   // Pacific
    'America/Anchorage',     // Alaska
    'Pacific/Honolulu'       // Hawaii
  ],
  // Canada
  'CA': [
    'America/St_Johns',      // Newfoundland
    'America/Halifax',       // Atlantic
    'America/Toronto',       // Eastern
    'America/Winnipeg',      // Central
    'America/Edmonton',      // Mountain
    'America/Vancouver'      // Pacific
  ],
  // Australia
  'AU': [
    'Australia/Perth',       // Western
    'Australia/Darwin',      // Central (no DST)
    'Australia/Adelaide',    // Central
    'Australia/Brisbane',    // Eastern (no DST)
    'Australia/Sydney',      // Eastern
    'Australia/Hobart'       // Tasmania
  ],
  // Russia
  'RU': [
    'Europe/Kaliningrad',    // UTC+2
    'Europe/Moscow',         // UTC+3
    'Europe/Samara',         // UTC+4
    'Asia/Yekaterinburg',    // UTC+5
    'Asia/Omsk',            // UTC+6
    'Asia/Krasnoyarsk',     // UTC+7
    'Asia/Irkutsk',         // UTC+8
    'Asia/Yakutsk',         // UTC+9
    'Asia/Vladivostok',     // UTC+10
    'Asia/Magadan',         // UTC+11
    'Asia/Kamchatka'        // UTC+12
  ],
  // Brazil
  'BR': [
    'America/Noronha',       // UTC-2
    'America/Belem',         // UTC-3
    'America/Sao_Paulo',     // UTC-3
    'America/Manaus',        // UTC-4
    'America/Rio_Branco'     // UTC-5
  ],
  // Mexico
  'MX': [
    'America/Tijuana',       // Pacific
    'America/Mazatlan',      // Mountain
    'America/Mexico_City',   // Central
    'America/Cancun'         // Eastern
  ],
  // Indonesia
  'ID': [
    'Asia/Jakarta',          // Western
    'Asia/Makassar',         // Central
    'Asia/Jayapura'          // Eastern
  ],
  // Kazakhstan
  'KZ': [
    'Asia/Almaty',           // UTC+6
    'Asia/Qyzylorda'         // UTC+5
  ],
  // Mongolia
  'MN': [
    'Asia/Ulaanbaatar',      // UTC+8
    'Asia/Hovd'              // UTC+7
  ],
  // Greenland
  'GL': [
    'America/Godthab',       // UTC-3
    'America/Danmarkshavn',  // UTC+0
    'America/Scoresbysund',  // UTC-1
    'America/Thule'          // UTC-4
  ]
};

// Get timezone display name
export const getTimezoneDisplayName = (timezone: string): string => {
  const names: { [key: string]: string } = {
    // US
    'America/New_York': 'Eastern Time (ET)',
    'America/Chicago': 'Central Time (CT)',
    'America/Denver': 'Mountain Time (MT)',
    'America/Phoenix': 'Arizona Time (MST - no DST)',
    'America/Los_Angeles': 'Pacific Time (PT)',
    'America/Anchorage': 'Alaska Time (AKT)',
    'Pacific/Honolulu': 'Hawaii Time (HST)',
    // Canada
    'America/St_Johns': 'Newfoundland Time (NT)',
    'America/Halifax': 'Atlantic Time (AT)',
    'America/Toronto': 'Eastern Time (ET)',
    'America/Winnipeg': 'Central Time (CT)',
    'America/Edmonton': 'Mountain Time (MT)',
    'America/Vancouver': 'Pacific Time (PT)',
    // Australia
    'Australia/Perth': 'Western Australia (AWST)',
    'Australia/Darwin': 'Northern Territory (ACST)',
    'Australia/Adelaide': 'South Australia (ACDT)',
    'Australia/Brisbane': 'Queensland (AEST)',
    'Australia/Sydney': 'New South Wales (AEDT)',
    'Australia/Hobart': 'Tasmania (AEDT)',
    // Russia
    'Europe/Kaliningrad': 'Kaliningrad (UTC+2)',
    'Europe/Moscow': 'Moscow (UTC+3)',
    'Europe/Samara': 'Samara (UTC+4)',
    'Asia/Yekaterinburg': 'Yekaterinburg (UTC+5)',
    'Asia/Omsk': 'Omsk (UTC+6)',
    'Asia/Krasnoyarsk': 'Krasnoyarsk (UTC+7)',
    'Asia/Irkutsk': 'Irkutsk (UTC+8)',
    'Asia/Yakutsk': 'Yakutsk (UTC+9)',
    'Asia/Vladivostok': 'Vladivostok (UTC+10)',
    'Asia/Magadan': 'Magadan (UTC+11)',
    'Asia/Kamchatka': 'Kamchatka (UTC+12)',
    // Brazil
    'America/Noronha': 'Fernando de Noronha (UTC-2)',
    'America/Belem': 'Brasília Time (BRT)',
    'America/Sao_Paulo': 'São Paulo (BRT/BRST)',
    'America/Manaus': 'Amazon Time (AMT)',
    'America/Rio_Branco': 'Acre Time (ACT)',
    // Mexico
    'America/Tijuana': 'Pacific Time (PT)',
    'America/Mazatlan': 'Mountain Time (MT)',
    'America/Mexico_City': 'Central Time (CT)',
    'America/Cancun': 'Eastern Time (ET)',
    // Indonesia
    'Asia/Jakarta': 'Western Indonesia (WIB)',
    'Asia/Makassar': 'Central Indonesia (WITA)',
    'Asia/Jayapura': 'Eastern Indonesia (WIT)',
    // Kazakhstan
    'Asia/Almaty': 'Almaty (UTC+6)',
    'Asia/Qyzylorda': 'Qyzylorda (UTC+5)',
    // Mongolia
    'Asia/Ulaanbaatar': 'Ulaanbaatar (UTC+8)',
    'Asia/Hovd': 'Hovd (UTC+7)',
    // Greenland
    'America/Godthab': 'West Greenland (UTC-3)',
    'America/Danmarkshavn': 'East Greenland (UTC+0)',
    'America/Scoresbysund': 'Scoresbysund (UTC-1)',
    'America/Thule': 'Thule (UTC-4)'
  };
  
  return names[timezone] || timezone;
};

// Check if a country has multiple timezones
export const hasMultipleTimezones = (countryCode: string): boolean => {
  return countryTimezones[countryCode]?.length > 1;
};

// Get timezones for a country
export const getCountryTimezones = (countryCode: string): string[] => {
  return countryTimezones[countryCode] || [];
};
